import {IReg, REG} from "lib/commons/registry";
import {BaseElementAsync, OSkinableInit} from "back/commons/basis";
import {DOM, JSX} from "lib/commons/xml/dom";
import {Button} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {FORMS, IFormJsonisable} from "lib/commons/forms";
import {BaseAreaView, IView, OBaseAreaViewInit} from "lib/commons/views";
import {IBasicUniversePointer} from "lib/core/universe";
import {DOMSH} from "lib/commons/xml/domsh";
import {OTabsInit, Tabs} from "back/commons/widgets/tabs";
import {ERROR} from "lib/core/errorReport";
import {CheckPrefArea, IUserPrefEditorCtx, SelectPrefArea, UserPrefPageConfig} from "back/core/dialogs/userPrefEditorConfig";
import {AppFrameDeskFeat} from "back/core/appFrame";

/**
 * Écran de modifification des préférences d'un utilisateur.
 */
export interface UserPrefEditor extends BaseElementAsync, IView {
	initialize(init: OUserPrefEditorInit): this
}

export interface OUserPrefEditorInit extends OSkinableInit {
	/** Reg de l'environnement graphique de ce UserPrefEditor. */
	reg?: IReg<IBasicUniversePointer>

	/** Liste des éditors (via fonction pour lazyLoading). */
	prefEditors?: Array<UserPrefPageConfig | (() => Promise<UserPrefPageConfig>)>

	/**
	 * Liste des fournisseurs d'éditors par une liste du reg (si prefEditors null).
	 * Type des entrées : (UserPrefEditorConfig | (() => Promise<UserPrefEditorConfig>))[]
	 */
	prefEditorsList?: string
}

/**
 *
 */
export class UserPrefEditor extends BaseElementAsync implements IView {
	reg: IReg<IBasicUniversePointer>

	//config: OUserPrefEditorInit;

	//protected _revertBtn: Button;
	protected _saveBtn: Button;
	protected _singleForm?: HTMLFormElement;
	protected _singleEditorConfig?: UserPrefPageConfig;
	protected _multiForms?: Tabs<UserPrefEditor>;

	get currentForm(): HTMLFormElement | null {return this._singleForm || this._multiForms?.selectedTab?.view as HTMLFormElement}

	get currentEditorConfig(): UserPrefPageConfig | null {return this._singleEditorConfig || this._multiForms.selectedTab?.area as UserPrefPageConfig}

	protected async _initialize(init: OUserPrefEditorInit) {
		const reg = this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		reg.installSkin("webzone:panel", sr);
		reg.installSkin("standard-dialog", sr);
		reg.installSkin("scroll/small", sr);
		this._initAndInstallSkin(this.localName, init);

		//this._revertBtn = new Button().initialize({uiContext: "dialog", label: "Rétablir", disabled: true});
		this._saveBtn = new Button().initialize({uiContext: "dialog", label: "Enregistrer", disabled: true});
		const closeBtn = new Button().initialize({uiContext: "dialog", label: "Fermer"});

		const edPromises = (init.prefEditors || reg.getList(init.prefEditorsList))?.map((ed) => typeof ed === "function" ? ed() : ed);
		const editors: UserPrefPageConfig[] = edPromises ? (await Promise.all(edPromises)).filter((ed) => ed.isVisible(this)) : [];

		switch (editors.length) {
		case 0:
			sr.appendChild(<c-msg label="Aucune préférence disponible"/>);
			break;
		case 1:
			this._singleEditorConfig = editors[0];
			this._singleForm = sr.appendChild(await editors[0].loadBody(this));
			break;
		default:
			this._multiForms = sr.appendChild(<Tabs î={{
				reg,
				areas: editors,
				areasContext: this
			} as OTabsInit<UserPrefEditor>}/>) as Tabs<UserPrefEditor>;
			await this._multiForms.initializedAsync;
		}

		sr.appendChild(<div id="footer">{this._saveBtn}{closeBtn}</div>);

		this.currentForm?.checkValidity();

		this._close = closeBtn.onclick = this.close.bind(this);
		reg.env.universe.auth.listeners.on('loggedUserChanged', this._close); //close, pas reload : liste des editors pourrait varier en fct des perms.

		const checkDirtyState = () => {this._saveBtn.disabled = !this.isPrefsDirty()};
		this.addEventListener('input', checkDirtyState);
		this.addEventListener('change', checkDirtyState);

		this._saveBtn.onclick = function (this: Button) {
			DOMSH.findHost<UserPrefEditor>(this)?.save(true);
		}
	}

	isPrefsDirty(): boolean {
		const form = this.currentForm;
		if (!form) return false;
		return DOM.findFirstChild(form, function (n: Node): n is IUserPrefElement {return ('isDirtyState' in n) && (n as IUserPrefElement).isDirtyState()}) != null;
	}


	isRelaodAppNeeded(): boolean {
		const form = this.currentForm;
		if (!form) return false;
		return DOM.findFirstChild(form, function (n: Node): n is IUserPrefElement {return ('isReloadAppNeeded' in n) && (n as IUserPrefElement).isReloadAppNeeded()}) != null;
	}

	async save(close?:boolean): Promise<boolean> {
		const btn = this._saveBtn;
		if (btn.disabled) return true;
		btn.disabled = true;
		try {
			const form = this.currentForm;
			if (form?.reportValidity()) {
				const datas = FORMS.formToJson(form);
				await this.currentEditorConfig.persistUserDatas.setUserDatas(datas);
			}
			if(close) this.close();
			if (AppFrameDeskFeat.isIn(desk) && this.isRelaodAppNeeded) await desk.reloadMainApp();
			return true;
		} catch (e) {
			await ERROR.report("Impossible d'enregistrer ces préférences", e);
			await this.reload();
			return false;
		} finally {
			btn.disabled = false;
		}
	}

	async reload() {
		if (this._singleForm) {
			const newForm = await this._singleEditorConfig.loadBody(this);
			this._singleForm.replaceWith(newForm);
			this._singleForm = newForm;
		} else {
			return this._multiForms.selectedTab?.rebuildView();
		}
	}

	close() {
		POPUP.findPopupableParent(this)?.close();
	}

	protected _close: (this: void) => void

	onViewHidden(closed?: boolean): void {
		if (closed && this._close) {
			this.reg.env.universe.auth.listeners.removeListener('loggedUserChanged', this._close);
		}
	}

	//Appelé aussi par le form dans chaque tab, cf UserPrefPageConfig.loadBody()
	onViewBeforeHide(close?: boolean): boolean {return !this.isPrefsDirty()}

	//Appelé aussi par le form dans chaque tab, cf UserPrefPageConfig.loadBody()
	async onViewWaitForHide(close?: boolean): Promise<boolean> {
		if (this.isPrefsDirty()) {
			if(await POPUP.confirm("Des préférences ont été modifiées.", this, {okLbl: "Enregistrer", cancelLbl: "Abandonner les modifications"})) {
				return this.save();
			} else if(!close) {
				await this.reload();
			}
		}
		return true;
	}
}

REG.reg.registerSkin('c-userpref-editor', 1, /* language=CSS */ `
	form {
		flex: 1;
		overflow-y: auto;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		padding: 1em;
	}

	[hidden] {
		display: none;
	}

	c-tabs {
		flex: 1 1 50vh;
	}

	c-msg {
		margin: .5em;
	}
`);

customElements.define('c-userpref-editor', UserPrefEditor);


/**
 * Widget constituant une préférence utilisateur (ou un regroupement de prefs).
 */
export interface IUserPrefElement extends HTMLElement, IFormJsonisable {
	/** Ce field est-il différent de son dernier état d'enregistrement. */
	isDirtyState(): boolean

	/** Si ce champ est modifié, exige-t-il que la app courrante soit rechargée. */
	isReloadAppNeeded(): boolean

	/** Retour à l'état par défaut du système. */
	resetSystem(): void

	//Recherche dans les prefs. A envisager si le nombre augmente...
	//Autre approche : Api sur l'area des widgets s'ils n'étaient pas tous instanciés (dans les pages non construites...).
	//highlight(re:RegExp|null): boolean

	//Pb typage TS incompatible entre HTMLElement et IFormElement qui extends Element
	addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void;

	removeEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions): void;
}

/** Option de type checkBox. */
export interface UserPrefCheck {
	initialize(init?: OUserPrefCheckInit): this
}

export interface OUserPrefCheckInit extends OBaseAreaViewInit<IUserPrefEditorCtx> {
}

export class UserPrefCheck extends BaseAreaView<IUserPrefEditorCtx> implements IUserPrefElement {

	area: CheckPrefArea

	protected _input: HTMLInputElement;

	get keyPref() {return this.area.getId()}

	get defaultState(): boolean {return this.areaContext.reg.getPref(this.keyPref) ?? this.area.checkStateIfNone}

	get currentState(): boolean {return this.areaContext.editorConfig.persistUserDatas.getUserData(this.keyPref) ?? this.defaultState}

	get checked() {return this.area.invertCheck ? !this._input.checked : this._input.checked}

	set checked(st: boolean) {
		this._input.checked = this.area.invertCheck ? !st : st;
	}

	protected _initialize(init: OUserPrefCheckInit) {
		super._initialize(init);
		this._input = <input id="check" type="checkbox"/> as HTMLInputElement;
		this._attach(this.localName, init,
			<label for="check">
				<div class="title">{this.area.getLabel(init.areaContext)}</div>
				<div class="details">{this.area.getDescription(init.areaContext)}</div>
			</label>,
			this._input
		);
		this.reg.installSkin("form-control-areas", this.shadowRoot);
		this.reg.installSkin("c-userpref-common", this.shadowRoot);
		this.checked = this.currentState;
	}

	fillJson(parent: Jsonisable, root: Jsonisable) {
		//if(this.defaultState !== this.checked) Utilisation indeterminate state ?
		parent[this.keyPref] = this.checked;
	}

	//not used
	extractJson(parent: Dict<any>): boolean {return false}

	isDirtyState(): boolean {
		return this.checked != this.currentState;
	}

	isReloadAppNeeded(): boolean {
		return this.area.isReloadAppNeeded;
	}

	resetSystem(): void {
		this.checked = this.defaultState;
	}
}

REG.reg.registerSkin('c-userpref-check', 1, /* language=CSS */ ``);
customElements.define('c-userpref-check', UserPrefCheck);


/** Option de type checkBox. */
export interface UserPrefSelect {
	initialize(init?: OUserPrefSelectInit): this
}

export interface OUserPrefSelectInit extends OBaseAreaViewInit<IUserPrefEditorCtx> {
}

export class UserPrefSelect extends BaseAreaView<IUserPrefEditorCtx> implements IUserPrefElement {

	area: SelectPrefArea

	protected _input: HTMLSelectElement;

	get keyPref() {return this.area.getId()}

	get defaultState(): string {return this.areaContext.reg.getPref(this.keyPref) ?? ""}

	protected _initialize(init: OUserPrefSelectInit) {
		super._initialize(init);
		this._input = <select id="select"/> as HTMLSelectElement;
		this.area.buildOptions(this._input, this.areaContext.editorConfig.persistUserDatas.getUserData(this.keyPref) ?? this.defaultState);
		this._attach(this.localName, init,
			<label for="select">
				<div class="title">{this.area.getLabel(init.areaContext)}</div>
				<div class="details">{this.area.getDescription(init.areaContext)}</div>
			</label>,
			this._input
		);
		this.reg.installSkin("form-control-areas", this.shadowRoot);
		this.reg.installSkin("c-userpref-common", this.shadowRoot);
	}

	fillJson(parent: Jsonisable, root: Jsonisable) {
		const v = this._input.value;
		//suppr de la pref si ""
		parent[this.keyPref] = v == "" ? undefined : v;
	}

	//not used
	extractJson(parent: Dict<any>): boolean {return false}

	isDirtyState(): boolean {
		return this._input.value != (this.areaContext.editorConfig.persistUserDatas.getUserData(this.keyPref) ?? "");
	}

	isReloadAppNeeded(): boolean {
		return this.area.isReloadAppNeeded;
	}

	resetSystem(): void {
		this._input.value = this.defaultState;
	}
}

REG.reg.registerSkin('c-userpref-select', 1, /* language=CSS */ ``);
customElements.define('c-userpref-select', UserPrefSelect);


REG.reg.registerSkin('c-userpref-common', 1, /* language=CSS */ `
	:host {
		display: flex;
		flex-direction: row;
	}

	:focus {
		outline: var(--focus-outline);
	}

	label {
		flex: 1;
		max-width: 50vw;
		margin-block: .7em;
		margin-inline: 0 1em;
	}

	.title {
		font-weight: bold;
	}

	.details {
		color: var(--fade-color);
	}

	input, select {
		flex: 0 0 auto;
		align-self: center;
	}
`);